import { useState, useRef, useEffect } from 'react'
import { Search, FileSearch, CheckCircle, Scale, FileText } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import nimonikLogo from '../../assets/images/nimonik-logo.png'
import LanguageSelector from '../ui/LanguageSelector'

interface WelcomePageProps {
  onSendMessage: (message: string) => boolean
}

const suggestionCards = [
  {
    icon: FileSearch,
    labelKey: 'welcome.suggestionLabels.findDocuments',
    promptKey: 'welcome.suggestionPrompts.findDocuments',
  },
  {
    icon: CheckCircle,
    labelKey: 'welcome.suggestionLabels.locateProvision',
    promptKey: 'welcome.suggestionPrompts.locateProvision',
  },
  {
    icon: Scale,
    labelKey: 'welcome.suggestionLabels.compareRequirements',
    promptKey: 'welcome.suggestionPrompts.compareRequirements',
  },
  {
    icon: FileText,
    labelKey: 'welcome.suggestionLabels.summarizeRegulation',
    promptKey: 'welcome.suggestionPrompts.summarizeRegulation',
  },
]

const MAX_LENGTH = 10000

export default function WelcomePage({ onSendMessage }: WelcomePageProps) {
  const [message, setMessage] = useState('')
  const [isExpanded, setIsExpanded] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const { t } = useTranslation()

  // Auto-resize textarea and track if multiline
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
      const scrollHeight = textareaRef.current.scrollHeight
      textareaRef.current.style.height = scrollHeight + 'px'
      setIsExpanded(scrollHeight > 56)
    }
  }, [message])

  const handleSubmit = () => {
    if (message.trim()) {
      const sent = onSendMessage(message)
      if (sent) setMessage('')
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit()
    }
  }

  const handleCardClick = (promptKey: string) => {
    onSendMessage(t(promptKey))
  }

  // Show suggestions only when there's no message
  const showSuggestions = message.trim().length === 0

  return (
    <div className="flex-1 flex flex-col bg-white relative overflow-hidden">
      {/* Language Selector — top right of screen */}
      <div className="absolute top-5 right-4">
        <LanguageSelector />
      </div>

      <div className="flex-1 flex flex-col items-center justify-center p-6 w-full max-w-4xl mx-auto overflow-y-auto">

        {/* Hero Section */}
        <div className="text-center mb-10 fade-in-up">
          <div className="w-20 h-20 bg-white rounded-xl mx-auto flex items-center justify-center mb-6 p-2">
            <img
              src={nimonikLogo}
              alt="Nimonik Logo"
              className="h-full object-contain"
            />
          </div>
          <h1 className="text-3xl font-semibold text-gray-800 mb-3 tracking-tight">
            {t('welcome.heroTitle')}
          </h1>
          <p className="text-gray-500 text-base max-w-lg mx-auto">
            {t('welcome.heroSubtitle')}
          </p>
          <div className="bg-yellow-50 border border-yellow-200 rounded-md px-2.5 py-0.5 mt-4 mx-auto w-full max-w-2xl">
            <p className="text-sm text-yellow-800 leading-tight p-1.5">
              {t('welcome.noticeBanner')}
            </p>
          </div>
        </div>

        {/* Input Box */}
        <div className="w-full max-w-2xl mb-8 fade-in-up delay-100 flex items-center gap-2 border border-gray-200 rounded-2xl bg-white shadow-sm hover:shadow-md transition-all focus-within:ring-2 focus-within:ring-brand-100 focus-within:border-brand group">
          <div className="pl-4 flex items-center pointer-events-none">
            <Search className="w-5 h-5 text-gray-400 group-focus-within:text-brand transition-colors" />
          </div>
          <textarea
            ref={textareaRef}
            rows={1}
            placeholder={t('welcome.placeholder')}
            value={message}
            onChange={(e) => setMessage(e.target.value.slice(0, MAX_LENGTH))}
            onKeyDown={handleKeyDown}
            maxLength={MAX_LENGTH}
            className={`flex-1 py-4 bg-transparent border-none resize-none outline-none focus:ring-0 focus:border-none text-sm text-gray-900 placeholder-gray-500 max-h-48 ${
              isExpanded ? 'overflow-y-auto' : 'overflow-hidden'
            }`}
          />
          <button
            onClick={handleSubmit}
            className="self-center mr-3 p-1.5 bg-brand text-white rounded-lg hover:bg-brand-hover transition duration-150 shadow-sm opacity-0 group-focus-within:opacity-100"
          >
            <svg className="w-5 h-5 transform rotate-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
          </button>
        </div>

        {/* Character counter */}
        {message.length >= MAX_LENGTH * 0.8 && (
          <p className={`text-xs w-full max-w-2xl text-center -mt-6 mb-2 ${message.length >= MAX_LENGTH ? 'text-red-500' : 'text-gray-400'}`}>
            {message.length}/{MAX_LENGTH} characters
          </p>
        )}

        {/* Suggestion Cards - Hidden when typing */}
        {showSuggestions && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full max-w-2xl fade-in-up delay-200">
            {suggestionCards.map((card, index) => (
              <button
                key={index}
                onClick={() => handleCardClick(card.promptKey)}
                className="text-left p-4 bg-white border border-gray-100 rounded-xl hover:border-brand-100 hover:bg-brand-50 hover:shadow-sm transition-all group"
              >
                <div className="flex items-center">
                  <span className="p-2 bg-gray-50 rounded-lg text-gray-400 group-hover:text-brand group-hover:bg-white mr-3 transition-colors flex-shrink-0">
                    <card.icon className="w-5 h-5" />
                  </span>
                  <span className="text-sm text-gray-500 group-hover:text-brand transition-colors">{t(card.labelKey)}</span>
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Footer */}
        <div className="mt-12 text-center fade-in-up delay-300">
          <p className="text-sm text-gray-400">
            {t('welcome.footerDisclaimer')}
          </p>
        </div>
      </div>
    </div>
  )
}
